import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { formatUsd } from "../lib/format";

type PublicTrade = {
  price_cents: number | string;
  quantity_shares: number | string;
  created_at: string;
  outcome: string;
  question: string;
};

type PublicProfile = {
  username: string;
  created_at: string;
  display_name: string | null;
  bio: string | null;
};

type ProfileResponse = {
  profile: PublicProfile;
  stats: { markets: number | string; realized: number | string };
  trades: PublicTrade[];
  tradesPublic: boolean;
};

export function ProfilePage() {
  const { username } = useParams();
  const q = useQuery({
    queryKey: ["profile", username],
    queryFn: () => api<ProfileResponse>(`/profile/${encodeURIComponent(username ?? "")}`),
    enabled: !!username,
    retry: false,
  });

  if (q.isLoading) {
    return (
      <div className="wrap">
        <div className="skeleton" style={{ height: 28, maxWidth: 280 }} />
      </div>
    );
  }

  if (q.isError || !q.data) {
    return (
      <div className="wrap empty">
        <h3>Profile not found</h3>
        <p>This trader is private, or the username does not exist.</p>
        <Link to="/leaderboard" className="btn btn-primary">Leaderboard</Link>
      </div>
    );
  }

  const { profile, stats, trades, tradesPublic } = q.data;
  const name = profile.display_name || profile.username;
  const joined = profile.created_at ? new Date(profile.created_at).toLocaleDateString(undefined, { month: "short", year: "numeric" }) : "";
  const letter = (name[0] ?? "?").toUpperCase();

  return (
    <div className="wrap">
      <div className="page-head profile-head">
        <span className="kicker">Public profile</span>
        <div className="profile-identity">
          <span className="profile-avatar" aria-hidden>{letter}</span>
          <div>
            <h1>{name}</h1>
            <p>@{profile.username}{joined ? ` · Joined ${joined}` : ""}</p>
          </div>
        </div>
        {profile.bio ? <p className="profile-bio">{profile.bio}</p> : null}
      </div>
      <div className="grid-2 profile-stats">
        <div className="card">
          <span className="kicker">Realized P/L</span>
          <strong>{formatUsd(stats.realized ?? 0)}</strong>
          <p>Public, settled results only. Cash balance is never shown here.</p>
        </div>
        <div className="card">
          <span className="kicker">Markets</span>
          <strong>{Number(stats.markets ?? 0)}</strong>
          <p>Contracts this trader has held a position in.</p>
        </div>
      </div>
      <h2 className="profile-fills-h">Recent fills</h2>
      {!tradesPublic ? (
        <div className="card empty" style={{ padding: 24 }}>
          <h3>Fills are private</h3>
          <p>This trader shares a profile, not a trade tape.</p>
        </div>
      ) : trades.length === 0 ? (
        <div className="card empty" style={{ padding: 24 }}>
          <h3>No public fills yet</h3>
          <p>When this trader matches an order, it can show here.</p>
        </div>
      ) : (
        <div className="table-scroll">
          <table className="table">
            <thead>
              <tr>
                <th>Market</th>
                <th>Side</th>
                <th>Price</th>
                <th>Size</th>
                <th>When</th>
              </tr>
            </thead>
            <tbody>
              {trades.map((t, i) => (
                <tr key={`${t.created_at}-${i}`}>
                  <td>{t.question}</td>
                  <td>{t.outcome}</td>
                  <td>{t.price_cents}¢</td>
                  <td>{t.quantity_shares}</td>
                  <td>{new Date(t.created_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
